import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { familyService } from '../services';
import { useAuth } from '../context/AuthContext';
import UserDetailModal from '../components/UserDetailModal';
import {
  UserPlus, Mail, CheckCircle, AlertCircle, X, Loader2,
  Heart, Crown, LogOut, Users, PlusCircle, Clock, HelpCircle, UserX, CheckCircle2,
} from 'lucide-react';

// ─── Role helpers ──────────────────────────────────────────────────────────────
const norm      = r  => (r || '').toLowerCase();                 // 'ADMIN' → 'admin'
const isParentR = r  => ['admin','parent'].includes(norm(r));
const isChildR  = r  => norm(r) === 'child';
const roleLabel = r  => { const n = norm(r); return n === 'admin' ? 'Admin' : n === 'parent' ? 'Parent' : 'Child'; };
const sexEmoji  = s  => s === 'male' || s === 'm' ? '👨' : s === 'female' || s === 'f' ? '👩' : '🧑';

// ─── Tree node ────────────────────────────────────────────────────────────────
function TreeNode({ member, index, testId, isAdmin, isCurrentUser, onRemove, onPromote, onViewDetails }) {
  const parentRole = isParentR(member.role);
  const name = member.display_name || member.name || `${roleLabel(member.role)} (${member.uid?.slice(0, 6)}…)`;
  const canPromote = isAdmin && !isCurrentUser && norm(member.role) === 'parent';
  const canRemove  = isAdmin && !isCurrentUser;

  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: 'easeOut' }}
      onClick={onViewDetails}
      className={`relative flex flex-col items-center text-center p-6 bg-white rounded-3xl
        shadow-[0_8px_30px_rgba(0,0,0,0.05)] border w-52 flex-shrink-0
        hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(0,0,0,0.09)]
        transition-all duration-300 z-10 cursor-pointer
        ${parentRole ? 'border-sky-100 ring-1 ring-sky-100' : 'border-violet-100 ring-1 ring-violet-100'}`}
    >
      {/* Admin action buttons */}
      {(canRemove || canPromote) && (
        <div className="absolute top-2 right-2 flex gap-1 z-20">
          {canPromote && (
            <button
              data-testid={`promote-${member.uid}`}
              onClick={e => { e.stopPropagation(); onPromote(member); }}
              title="Promote to Admin"
              className="p-1.5 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-lg transition-colors"
            >
              <Crown size={12} />
            </button>
          )}
          {canRemove && (
            <button
              data-testid={`remove-${member.uid}`}
              onClick={e => { e.stopPropagation(); onRemove(member); }}
              title="Remove member"
              className="p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors"
            >
              <X size={12} />
            </button>
          )}
        </div>
      )}

      {/* ADMIN crown badge */}
      {norm(member.role) === 'admin' && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
          <Crown size={9} /> Admin
        </div>
      )}

      {/* Avatar */}
      <div className={`w-20 h-20 rounded-full flex items-center justify-center text-4xl mb-4
        shadow-inner ring-4 ring-white
        ${parentRole ? 'bg-sky-100' : 'bg-violet-100'}`}>
        {member.avatar_url
          ? <img src={member.avatar_url} alt={name} className="w-full h-full rounded-full object-cover" />
          : <span>{sexEmoji(member.sex)}</span>}
      </div>

      <p className="font-semibold text-lg text-slate-800 tracking-tight line-clamp-1 w-full font-outfit">{name}</p>

      <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border mt-4
        ${parentRole ? 'bg-sky-50 text-sky-600 border-sky-100' : 'bg-violet-50 text-violet-600 border-violet-100'}`}>
        {roleLabel(member.role)}
      </span>
    </motion.div>
  );
}

// ─── Placeholder node ─────────────────────────────────────────────────────────
function PlaceholderNode({ role, index, onInvite }) {
  const isParent = isParentR(role);
  return (
    <motion.button
      data-testid={`add-${role}-placeholder`}
      onClick={onInvite}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className={`relative flex flex-col items-center justify-center text-center p-6
        bg-slate-50/60 rounded-3xl border-2 border-dashed w-52 h-52 flex-shrink-0
        cursor-pointer transition-all duration-300 group z-10
        ${isParent ? 'border-slate-300 hover:border-sky-400 hover:bg-sky-50/40' : 'border-slate-300 hover:border-violet-400 hover:bg-violet-50/30'}`}
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-3 transition-colors
        ${isParent ? 'bg-slate-100 group-hover:bg-sky-100' : 'bg-slate-100 group-hover:bg-violet-100'}`}>
        <UserPlus size={22} className={`text-slate-400 transition-colors ${isParent ? 'group-hover:text-sky-500' : 'group-hover:text-violet-500'}`} />
      </div>
      <p className={`font-semibold text-sm text-slate-500 font-outfit transition-colors ${isParent ? 'group-hover:text-sky-600' : 'group-hover:text-violet-600'}`}>
        Add {isParent ? 'Your Spouse' : 'Child'}
      </p>
      <p className="text-xs text-slate-400 mt-1">Click to invite</p>
    </motion.button>
  );
}

// ─── Pending Invite Node (locked slot showing invitee email + ? badge) ─────────
function PendingInviteNode({ invite, index }) {
  const parentRole = isParentR(invite.role);
  const displayName = invite.invitee_name || invite.invitee_email?.split('@')[0] || 'Invited';

  return (
    <motion.div
      data-testid={`pending-node-${invite.invitation_id}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: 'easeOut' }}
      className={`relative flex flex-col items-center text-center p-6 rounded-3xl
        border-2 border-dashed w-52 flex-shrink-0 z-10
        ${parentRole ? 'border-sky-200 bg-sky-50/40' : 'border-violet-200 bg-violet-50/30'}`}
    >
      {/* "?" Pending badge */}
      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-amber-400 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm whitespace-nowrap">
        <HelpCircle size={9} />  PENDING
      </div>

      {/* Avatar placeholder */}
      <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl mb-4 border-2 border-dashed
        ${parentRole ? 'bg-sky-100 border-sky-200' : 'bg-violet-100 border-violet-200'}`}>
        <Mail size={26} className={parentRole ? 'text-sky-400' : 'text-violet-400'} />
      </div>

      <p className="font-semibold text-base text-slate-700 tracking-tight line-clamp-1 w-full font-outfit">
        {displayName}
      </p>
      <p className="text-xs text-slate-400 mt-0.5 truncate w-full px-1" title={invite.invitee_email}>
        {invite.invitee_email}
      </p>

      <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border mt-3
        ${parentRole ? 'bg-sky-50 text-sky-600 border-sky-100' : 'bg-violet-50 text-violet-600 border-violet-100'}`}>
        {roleLabel(invite.role)}
      </span>

      <div className="flex items-center gap-1 mt-2 text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
        <Clock size={9} /> Awaiting response
      </div>
    </motion.div>
  );
}
function ConnectorSVG({ parentRefs, childRefs, containerRef }) {
  const [paths, setPaths] = useState([]);

  useLayoutEffect(() => {
    const recalc = () => {
      if (!containerRef.current) return;
      const box = containerRef.current.getBoundingClientRect();
      const pCenters = parentRefs.current.filter(Boolean).map(el => {
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2 - box.left, y: r.bottom - box.top };
      });
      const cCenters = childRefs.current.filter(Boolean).map(el => {
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2 - box.left, y: r.top - box.top };
      });
      if (!pCenters.length || !cCenters.length) return;
      const midX = pCenters.reduce((s, p) => s + p.x, 0) / pCenters.length;
      const pMaxY = Math.max(...pCenters.map(p => p.y));
      const cMinY = Math.min(...cCenters.map(c => c.y));
      const midY  = pMaxY + (cMinY - pMaxY) / 2;
      const computed = [];
      pCenters.forEach((p, i) => computed.push({ key: `p-${i}`, d: `M ${p.x} ${p.y} C ${p.x} ${midY}, ${midX} ${midY}, ${midX} ${midY}` }));
      cCenters.forEach((c, i) => computed.push({ key: `c-${i}`, d: `M ${midX} ${midY} C ${midX} ${midY}, ${c.x} ${midY}, ${c.x} ${c.y}` }));
      setPaths(computed);
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [parentRefs, childRefs, containerRef]);

  if (!paths.length) return null;
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" overflow="visible">
      {paths.map(({ key, d }) => (
        <motion.path key={key} d={d} stroke="#CBD5E1" strokeWidth="2.5" fill="none" strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.7, ease: 'easeInOut', delay: 0.25 }} />
      ))}
    </svg>
  );
}

// ─── Heart divider ────────────────────────────────────────────────────────────
const HeartRow = () => (
  <div className="flex items-center justify-center gap-3 w-full">
    <div className="h-px flex-1 bg-slate-200 max-w-xs" />
    <Heart size={14} className="text-rose-300 fill-rose-200 flex-shrink-0" />
    <div className="h-px flex-1 bg-slate-200 max-w-xs" />
  </div>
);

// ─── Invite Modal ─────────────────────────────────────────────────────────────
function InviteModal({ role, onClose, onSuccess }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail]       = useState('');
  const [message, setMessage]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await familyService.sendInvitation({
        email,
        fullName,
        role: role.toUpperCase(),           // 'child'→'CHILD', 'parent'→'PARENT'
        invitationMessage: message || undefined,
      });
      setSuccess(`Invitation sent to ${email}!`);
      setTimeout(() => { onSuccess(); onClose(); }, 1800);
    } catch (err) {
      const msg = err?.response?.data?.detail || '';
      setError(
        msg.toLowerCase().includes('capacity') || msg.toLowerCase().includes('exceed')
          ? 'Family capacity limit reached for this role.'
          : msg.toLowerCase().includes('already')
          ? 'This email has already been invited or is already in a family.'
          : msg || 'Failed to send invitation. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const inp = 'w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition-all';

  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div data-testid="invitation-modal"
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md"
        initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
        transition={{ duration: 0.2 }}>
        <div className="flex items-center justify-between px-8 pt-8 pb-4">
          <div>
            <h3 className="text-xl font-bold text-slate-900 font-outfit">
              Invite {role === 'child' ? 'a Child' : 'Your Spouse'}
            </h3>
            <p className="text-slate-500 text-sm mt-1">Send an invitation email to join your family</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-colors"><X size={18} /></button>
        </div>

        {success ? (
          <div className="px-8 pb-10 flex flex-col items-center gap-4 pt-4">
            <div className="w-16 h-16 bg-green-100 rounded-3xl flex items-center justify-center">
              <CheckCircle size={28} className="text-green-500" />
            </div>
            <p className="font-semibold text-slate-900">{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 pb-8 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Full Name</label>
              <input data-testid="invite-fullname-input" className={inp} value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Full name" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Email Address</label>
              <input data-testid="invite-email-input" type="email" className={inp} value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Personal Message <span className="text-slate-400 normal-case">(optional)</span></label>
              <textarea data-testid="invite-message-input" className={`${inp} resize-none`} rows={2} value={message} onChange={e => setMessage(e.target.value)} placeholder="Write a personal note…" />
            </div>
            {error && (
              <div data-testid="invite-error" className="p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm flex items-start gap-2">
                <AlertCircle size={15} className="flex-shrink-0 mt-0.5" /> {error}
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">Cancel</button>
              <button data-testid="send-invite-btn" type="submit" disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white rounded-2xl font-semibold text-sm transition-all">
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                {loading ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Confirm Modal ────────────────────────────────────────────────────────────
function ConfirmModal({ title, body, actionLabel, danger = true, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); onClose(); } };
  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div data-testid="confirm-modal" className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8"
        initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} transition={{ duration: 0.18 }}>
        <h3 className="text-xl font-bold text-slate-900 font-outfit mb-2">{title}</h3>
        <p className="text-slate-500 text-sm mb-6">{body}</p>
        <div className="flex gap-3">
          <button onClick={onClose} disabled={busy} className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">Cancel</button>
          <button data-testid="confirm-action-btn" onClick={handle} disabled={busy}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl font-semibold text-sm text-white transition-all
              ${danger ? 'bg-red-500 hover:bg-red-600 disabled:bg-red-300' : 'bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300'}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} {actionLabel}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Child invitation card — 3-step ACCEPT / REJECT confirms ─────────────────
function ChildInvitationCard({ invitation, onAccept, onReject }) {
  // step 0=idle, 1,2,3 = stacked confirm layers
  const [action, setAction]     = useState(null);  // 'accept' | 'reject'
  const [step,   setStep]       = useState(0);
  const [busy,   setBusy]       = useState(false);
  const [err,    setErr]        = useState('');

  const isAccept = action === 'accept';
  const start    = (a) => { setAction(a); setStep(1); setErr(''); };
  const cancel   = ()  => { setStep(0); setAction(null); };

  const handleFinal = async () => {
    setBusy(true);
    try {
      if (isAccept) await onAccept(invitation.invitation_id);
      else          await onReject(invitation.invitation_id);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Something went wrong. Please try again.');
      setStep(0); setAction(null);
    } finally {
      setBusy(false);
    }
  };

  const ACCEPT_STEPS = [
    { title: 'Join this family?',          body: `You're about to join as a ${roleLabel(invitation.role)}. You'll gain access to shared family resources and camps.`, yes: 'Yes, Continue' },
    { title: 'Are you absolutely sure?',   body: 'Once you join, your profile will be linked to this family. You will need to sign in again to apply the changes.', yes: 'Join Family' },
    { title: 'Ready to sign in again?',    body: 'Accepting will redirect you to sign in one more time to activate your new access.', yes: 'Accept & Sign In', final: true },
  ];
  const REJECT_STEPS = [
    { title: 'Decline this invitation?',   body: `You're about to decline the invitation to join as ${roleLabel(invitation.role)}.`, yes: 'Yes, Decline' },
    { title: 'Are you sure?',              body: 'This invitation will be removed. You\'ll need to ask the family admin to invite you again.', yes: 'Decline Invitation' },
    { title: 'Final confirmation',         body: 'This action is irreversible. The invitation will be permanently declined.', yes: 'Decline Forever', final: true },
  ];
  const steps = isAccept ? ACCEPT_STEPS : REJECT_STEPS;
  const zLevels = [60, 70, 80];

  return (
    <>
      <div
        data-testid={`invitation-card-${invitation.invitation_id}`}
        className="bg-white rounded-3xl border border-sky-100 shadow-sm overflow-hidden"
      >
        <div className="h-1 bg-gradient-to-r from-sky-400 to-blue-500" />
        <div className="p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-12 h-12 bg-sky-50 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Users size={20} className="text-sky-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 text-sm">
                Invited as <span className="text-sky-600">{roleLabel(invitation.role)}</span>
              </p>
              {invitation.invitee_name && (
                <p className="text-xs text-slate-600 mt-0.5 font-medium">{invitation.invitee_name}</p>
              )}
              <p className="text-xs text-slate-400 mt-0.5 truncate">{invitation.invitee_email}</p>
              <div className="flex items-center gap-1.5 mt-2">
                <Clock size={10} className="text-slate-400" />
                <p className="text-xs text-slate-400">
                  Expires {new Date(invitation.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>

          {err && (
            <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-1.5">
              <AlertCircle size={12} /> {err}
            </div>
          )}

          <div className="flex gap-2">
            <button
              data-testid={`reject-invite-${invitation.invitation_id}`}
              onClick={() => start('reject')}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 hover:bg-red-50 hover:border-red-200 hover:text-red-500 text-sm font-semibold transition-all"
            >
              <UserX size={13} /> Decline
            </button>
            <button
              data-testid={`accept-invite-${invitation.invitation_id}`}
              onClick={() => start('accept')}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-all"
            >
              <CheckCircle2 size={13} /> Accept & Join
            </button>
          </div>
        </div>
      </div>

      {/* Stacked 3-step confirmation modals */}
      {steps.map((s, idx) => (
        step >= idx + 1 && (
          <motion.div
            key={idx}
            className={`fixed inset-0 z-[${zLevels[idx]}] flex items-center justify-center p-4 bg-slate-900/25 backdrop-blur-[2px]`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div
              data-testid={`invite-confirm-step${idx + 1}-${action}`}
              className={`bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 ${s.final && !isAccept ? 'border-2 border-red-200' : s.final && isAccept ? 'border-2 border-sky-200' : ''}`}
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.18 }}
            >
              {s.final && (
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 ${isAccept ? 'bg-sky-100' : 'bg-red-100'}`}>
                  {isAccept ? <CheckCircle2 size={22} className="text-sky-500" /> : <UserX size={22} className="text-red-500" />}
                </div>
              )}
              <h3 className={`text-xl font-bold text-slate-900 font-outfit mb-2 ${s.final ? 'text-center' : ''}`}>{s.title}</h3>
              <p className={`text-slate-500 text-sm mb-6 ${s.final ? 'text-center' : ''}`}>{s.body}</p>
              <div className="flex gap-3">
                <button
                  onClick={idx === 0 ? cancel : () => setStep(idx)}
                  disabled={busy}
                  className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  {idx === 0 ? 'Cancel' : 'Go Back'}
                </button>
                <button
                  data-testid={`invite-confirm-btn-${idx + 1}`}
                  onClick={s.final ? handleFinal : () => setStep(idx + 2)}
                  disabled={busy}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl font-semibold text-sm transition-all disabled:opacity-50
                    ${isAccept ? 'bg-sky-500 hover:bg-sky-600 text-white' : (s.final ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-500 hover:bg-red-600 text-white')}`}
                >
                  {busy && s.final ? <Loader2 size={13} className="animate-spin" /> : null}
                  {busy && s.final ? (isAccept ? 'Joining…' : 'Declining…') : s.yes}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )
      ))}
    </>
  );
}

// ─── No-Family panel ──────────────────────────────────────────────────────────
function NoFamilyPanel({ invitations, onCreateFamily, onAcceptInvite, onRejectInvite, creating, isChild }) {
  // ── CHILD VIEW ──────────────────────────────────────────────────────────────
  if (isChild) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto">
        {invitations.length === 0 ? (
          // No invitations — ask parent to invite
          <div className="bg-white rounded-3xl border border-slate-200/60 p-10 text-center shadow-sm">
            <div className="w-16 h-16 bg-sky-50 rounded-3xl flex items-center justify-center mx-auto mb-5">
              <Mail size={28} className="text-sky-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 font-outfit mb-2">Waiting for an Invitation</h3>
            <p className="text-slate-500 text-sm leading-relaxed max-w-xs mx-auto">
              Ask your parent or guardian to invite you to their family group. Your invitation will appear here once sent.
            </p>
          </div>
        ) : (
          // Has pending invitations
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-sky-100 rounded-xl flex items-center justify-center">
                <Mail size={15} className="text-sky-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  You have {invitations.length} pending invitation{invitations.length > 1 ? 's' : ''}
                </p>
                <p className="text-xs text-slate-400">Review and accept or decline below</p>
              </div>
            </div>
            <div className="space-y-3">
              {invitations.map(inv => (
                <ChildInvitationCard
                  key={inv.invitation_id}
                  invitation={inv}
                  onAccept={onAcceptInvite}
                  onReject={onRejectInvite}
                />
              ))}
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  // ── PARENT / ADMIN VIEW ─────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 max-w-lg mx-auto">
      {/* Create family */}
      <div className="bg-white rounded-3xl border border-slate-200/60 p-8 text-center shadow-sm">
        <div className="w-16 h-16 bg-sky-100 rounded-3xl flex items-center justify-center mx-auto mb-4">
          <PlusCircle size={28} className="text-sky-500" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 font-outfit mb-2">Start Your Family</h3>
        <p className="text-slate-500 text-sm mb-6">Create a family group to manage camp enrollments and coaching for your children.</p>
        <button data-testid="create-family-btn" onClick={onCreateFamily} disabled={creating}
          className="inline-flex items-center gap-2 px-8 py-3 bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white rounded-2xl font-semibold transition-all shadow-sm">
          {creating ? <Loader2 size={16} className="animate-spin" /> : <PlusCircle size={16} />}
          {creating ? 'Creating…' : 'Create Family'}
        </button>
      </div>

      {/* Pending inbound invitations for parent too */}
      {invitations.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
            <Mail size={15} className="text-sky-500" /> Pending Invitations
          </p>
          <div className="space-y-3">
            {invitations.map(inv => (
              <ChildInvitationCard key={inv.invitation_id} invitation={inv} onAccept={onAcceptInvite} onReject={onRejectInvite} />
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function MyFamily() {
  const { familyRole: jwtRole, user, reauthWithRedirect, onboardingRole } = useAuth();

  const [family, setFamily]           = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [creating, setCreating]       = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  // Modals
  const [inviteModal, setInviteModal]         = useState(null);  // null | 'child' | 'parent'
  const [confirmModal, setConfirmModal]       = useState(null);  // null | {action, uid?, name?}
  const [userDetailMember, setUserDetailMember] = useState(null); // null | member object

  // SVG connector refs
  const containerRef = useRef(null);
  const parentRefs   = useRef([]);
  const childRefs    = useRef([]);

  const currentUid = user?.uid || '';

  // ─── Load family data on mount ────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [familyRes, invitationsRes] = await Promise.allSettled([
        familyService.getFamily(),
        familyService.listInvitations(),
      ]);

      // 205 Reset Content — JWT is stale (no familyId claim). Trigger the same
      // reauthWithRedirect() flow used after create/accept so beforeSignIn can
      // issue a fresh JWT with familyId + familyRole claims.
      if (familyRes.status === 'rejected' && familyRes.reason?.code === 'STALE_TOKEN') {
        setLoading(false);
        await reauthWithRedirect();
        return;
      }

      setFamily(familyRes.status === 'fulfilled' ? familyRes.value : null);
      setInvitations(invitationsRes.status === 'fulfilled'
        ? (Array.isArray(invitationsRes.value) ? invitationsRes.value : (invitationsRes.value?.invitations || []))
        : []);
    } catch {
      setError('Failed to load family data. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [reauthWithRedirect]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── After create/accept: force-refresh JWT then retry GET /families ──────
  // The APIGW reads family_id from the JWT claim (X-Auth-FamilyId).
  // After a create/accept, Firebase JWT is enriched asynchronously via
  // the sls-gcp-identity-platform beforeSignIn event. We force-refresh and
  // retry with backoff to bridge the eventual-consistency gap.
  const refreshAndDispatch = () => {
    fetchData();
    window.dispatchEvent(new CustomEvent('familyStatusChanged'));
  };

  // ── Actions ──
  const handleCreateFamily = async () => {
    setCreating(true);
    try {
      await familyService.createFamily();
      // Family created — reauth so beforeSignIn issues a fresh token with
      // the new family_id / family_role claims.
      await reauthWithRedirect();
    } catch (err) {
      // 401 / 403 are handled globally by the Axios interceptor (SessionExpiredModal).
      // Only surface other errors here.
      const status = err?.response?.status;
      if (status === 401 || status === 403) return;
      const msg = err?.response?.data?.detail || '';
      setError(msg.includes('already') ? 'You are already in a family.' : msg || 'Failed to create family.');
    } finally {
      setCreating(false);
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (invitationId) => {
    setLoading(true);
    try {
      await familyService.acceptInvitation(invitationId);
      // 5-second buffer: wait for the FamilyMemberInvited event to propagate
      // through the backend → GCP Identity Platform before triggering reauth.
      // Triggering too early means the fresh JWT won't carry the new family claims.
      setSyncMessage('Securing your access… please wait a moment.');
      await new Promise(resolve => setTimeout(resolve, 5000));
      setSyncMessage('');
      await reauthWithRedirect();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to accept invitation.');
      setSyncMessage('');
      setLoading(false);
    }
  };

  const handleRejectInvite = async (invitationId) => {
    try {
      await familyService.rejectInvitation(invitationId);
      // Refresh invitation list
      const res = await familyService.listInvitations().catch(() => []);
      setInvitations(Array.isArray(res) ? res : (res?.invitations || []));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to decline invitation. Please try again.');
    }
  };

  const handleRemoveMember = async (uid) => {
    await familyService.removeMember(uid);
    refreshAndDispatch();
  };

  const handlePromote = async (uid) => {
    await familyService.updateMemberRole(uid, 'ADMIN');
    refreshAndDispatch();
  };

  const handleLeave = async () => {
    try {
      await familyService.leaveFamily();
      refreshAndDispatch();
    } catch (err) {
      const code = err?.response?.data?.error_code;
      if (err?.response?.status === 400 && code === '7003908') {
        setError("You're the last member in the family. You cannot leave — the family must have at least one member.");
      } else {
        setError(err?.response?.data?.error_message || 'Failed to leave family. Please try again.');
      }
    } finally {
      setConfirmModal(null);
    }
  };

  // ── Derived state ──
  const members       = family?.members || [];
  const currentMember = members.find(m => m.uid === currentUid);
  const myRole        = norm(currentMember?.role || jwtRole || 'parent');
  const isAdmin       = myRole === 'admin' || myRole === 'parent';
  const isActualAdmin = myRole === 'admin';
  const hasFamily     = !!family;
  const isLastMember  = members.length === 1;
  // Child detection: use JWT role OR advisory profile.family_role (for pre-family state)
  const isChildRole   = norm(jwtRole) === 'child' || norm(onboardingRole) === 'child';

  // Group members
  const parentMembers = members.filter(m => isParentR(m.role));
  const childMembers  = members.filter(m => isChildR(m.role));

  // pending invitations — only PENDING ones
  const pendingInvites      = invitations.filter(i => i.status === 'PENDING');
  const pendingParentInvites = pendingInvites.filter(i => isParentR(i.role));
  const pendingChildInvites  = pendingInvites.filter(i => isChildR(i.role));

  // Reset refs
  parentRefs.current = [];
  childRefs.current  = [];

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3">
      <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
      {syncMessage && (
        <p className="text-sm text-slate-500 animate-pulse">{syncMessage}</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Page header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <p className="text-sky-500 text-xs font-bold uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
              <span className="w-6 h-px bg-sky-400 inline-block" /> My Family
            </p>
            <h1 className="text-4xl font-bold text-slate-900 font-outfit">🏡 My Family</h1>
            <p className="text-slate-500 mt-1">
              {hasFamily ? 'Your family circle' : 'Set up your family to get started'}
            </p>
          </div>

          {/* Admin actions */}
          {hasFamily && isAdmin && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button data-testid="invite-child-btn"
                onClick={() => setInviteModal('child')}
                className="flex items-center gap-1.5 px-4 py-2 bg-violet-500 hover:bg-violet-600 text-white rounded-xl text-sm font-semibold transition-all shadow-sm">
                <UserPlus size={14} /> Add Child
              </button>
              <button data-testid="invite-parent-btn"
                onClick={() => setInviteModal('parent')}
                className="flex items-center gap-1.5 px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-xl text-sm font-semibold transition-all shadow-sm">
                <UserPlus size={14} /> Add Your Spouse
              </button>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm flex items-center gap-2">
            <AlertCircle size={16} /> {error}
            <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* ── Has family ── */}
        {hasFamily ? (
          <>
            {/* Status banner */}
            <div data-testid="family-status-banner"
              className="rounded-2xl p-4 mb-10 border flex items-center gap-3 bg-emerald-50/60 border-emerald-100 text-emerald-800">
              <CheckCircle size={18} className="flex-shrink-0" />
              <span className="text-sm font-medium">Family is active. You have {parentMembers.length} parent(s) and {childMembers.length} child(ren).</span>
              {isActualAdmin && (
                <span className="ml-auto flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2 py-0.5">
                  <Crown size={11} /> You are the Admin
                </span>
              )}
            </div>

            {/* Family Tree */}
            <div ref={containerRef} className="relative flex flex-col items-center gap-16 py-8 w-full">
              <div className="hidden md:block absolute inset-0 pointer-events-none z-0">
                <ConnectorSVG parentRefs={parentRefs} childRefs={childRefs} containerRef={containerRef} />
              </div>

              {/* Parents row */}
              <div className="flex flex-row flex-wrap justify-center gap-8 md:gap-20 z-10 relative">
                {parentMembers.map((m, i) => (
                  <div key={m.uid} ref={el => { parentRefs.current[i] = el; }}>
                    <TreeNode
                      member={m} index={i}
                      testId={`parent-node-${m.uid}`}
                      isAdmin={isAdmin}
                      isCurrentUser={m.uid === currentUid}
                      onRemove={m => setConfirmModal({ action: 'remove', uid: m.uid, name: m.display_name || m.name || m.uid })}
                      onPromote={m => setConfirmModal({ action: 'promote', uid: m.uid, name: m.display_name || m.name || m.uid })}
                      onViewDetails={() => setUserDetailMember(m)}
                    />
                  </div>
                ))}
                {/* Pending parent invites — locked slots */}
                {isAdmin && pendingParentInvites.map((inv, i) => (
                  <div key={inv.invitation_id} ref={el => { parentRefs.current[parentMembers.length + i] = el; }}>
                    <PendingInviteNode invite={inv} index={parentMembers.length + i} />
                  </div>
                ))}
                {/* Placeholder — only show when no pending parent invite */}
                {isAdmin && pendingParentInvites.length === 0 && (
                  <div ref={el => { parentRefs.current[parentMembers.length] = el; }}>
                    <PlaceholderNode role="parent" index={parentMembers.length} onInvite={() => setInviteModal('parent')} />
                  </div>
                )}
              </div>

              {/* Heart + children */}
              {childMembers.length > 0 && (
                <>
                  <div className="hidden md:flex w-full"><HeartRow /></div>
                  <div className="md:hidden w-0.5 h-8 bg-slate-200 rounded-full -my-8" />
                  <div className="flex flex-row flex-wrap justify-center gap-8 md:gap-12 z-10 relative">
                    {childMembers.map((m, i) => (
                      <div key={m.uid} ref={el => { childRefs.current[i] = el; }}>
                        <TreeNode
                          member={m} index={parentMembers.length + i}
                          testId={`child-node-${m.uid}`}
                          isAdmin={isAdmin}
                          isCurrentUser={m.uid === currentUid}
                          onRemove={m => setConfirmModal({ action: 'remove', uid: m.uid, name: m.display_name || m.name || m.uid })}
                          onPromote={() => {}}
                          onViewDetails={() => setUserDetailMember(m)}
                        />
                      </div>
                    ))}
                    {/* Pending child invites — locked slots */}
                    {isAdmin && pendingChildInvites.map((inv, i) => (
                      <div key={inv.invitation_id} ref={el => { childRefs.current[childMembers.length + i] = el; }}>
                        <PendingInviteNode invite={inv} index={parentMembers.length + childMembers.length + i} />
                      </div>
                    ))}
                    {/* Placeholder to invite more children */}
                    {isAdmin && (
                      <div ref={el => { childRefs.current[childMembers.length + pendingChildInvites.length] = el; }}>
                        <PlaceholderNode role="child" index={parentMembers.length + childMembers.length + pendingChildInvites.length} onInvite={() => setInviteModal('child')} />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* No children yet — show pending + placeholder */}
              {childMembers.length === 0 && isAdmin && (
                <>
                  <div className="hidden md:flex w-full"><HeartRow /></div>
                  <div className="flex flex-row flex-wrap justify-center gap-8 md:gap-12 z-10 relative">
                    {pendingChildInvites.map((inv, i) => (
                      <div key={inv.invitation_id} ref={el => { childRefs.current[i] = el; }}>
                        <PendingInviteNode invite={inv} index={parentMembers.length + i} />
                      </div>
                    ))}
                    <div ref={el => { childRefs.current[pendingChildInvites.length] = el; }}>
                      <PlaceholderNode role="child" index={parentMembers.length + pendingChildInvites.length} onInvite={() => setInviteModal('child')} />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Pending outgoing invitations summary */}
            {pendingInvites.length > 0 && (
              <div className="mt-6 border border-amber-100 bg-amber-50/40 rounded-2xl p-4">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <Mail size={13} /> {pendingInvites.length} Pending Invitation{pendingInvites.length > 1 ? 's' : ''}
                </p>
                <div className="space-y-2">
                  {pendingInvites.map(inv => (
                    <div key={inv.invitation_id} className="flex items-center gap-3 text-sm text-slate-600">
                      <span className={`w-2 h-2 rounded-full ${isChildR(inv.role) ? 'bg-violet-400' : 'bg-sky-400'}`} />
                      <span className="font-medium">{inv.invitee_name}</span>
                      <span className="text-slate-400">{inv.invitee_email}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold
                        ${isChildR(inv.role) ? 'bg-violet-100 text-violet-600' : 'bg-sky-100 text-sky-600'}`}>
                        {roleLabel(inv.role)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leave family */}
            <div className="mt-8 flex justify-end">
              <button
                data-testid="leave-family-btn"
                onClick={() => !isLastMember && setConfirmModal({ action: 'leave' })}
                disabled={isLastMember}
                title={isLastMember ? "You're the only member — you cannot leave" : undefined}
                className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-semibold transition-colors ${
                  isLastMember
                    ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                    : 'border-red-200 text-red-500 hover:bg-red-50 cursor-pointer'
                }`}
              >
                <LogOut size={14} /> Leave Family
              </button>
            </div>
          </>
        ) : (
          /* ── No family ── */
          <NoFamilyPanel
            invitations={pendingInvites}
            onCreateFamily={handleCreateFamily}
            onAcceptInvite={handleAcceptInvite}
            onRejectInvite={handleRejectInvite}
            creating={creating}
            isChild={isChildRole}
          />
        )}
      </div>

      {/* User Detail Modal (manages its own animation layers) */}
      {userDetailMember && (
        <UserDetailModal
          member={userDetailMember}
          isAdmin={isAdmin}
          isCurrentUser={userDetailMember.uid === currentUid}
          onRemove={async (m) => { await handleRemoveMember(m.uid); }}
          onClose={() => setUserDetailMember(null)}
        />
      )}

      {/* Other Modals */}
      <AnimatePresence>
        {inviteModal && (
          <InviteModal
            role={inviteModal}
            onClose={() => setInviteModal(null)}
            onSuccess={refreshAndDispatch}
          />
        )}
        {confirmModal?.action === 'remove' && (
          <ConfirmModal
            title="Remove Member"
            body={`Are you sure you want to remove ${confirmModal.name} from the family? This will revoke all their active entitlements.`}
            actionLabel="Remove"
            danger
            onConfirm={() => handleRemoveMember(confirmModal.uid)}
            onClose={() => setConfirmModal(null)}
          />
        )}
        {confirmModal?.action === 'promote' && (
          <ConfirmModal
            title="Promote to Admin"
            body={`Promote ${confirmModal.name} to Family Admin? They will gain full management permissions.`}
            actionLabel="Promote"
            danger={false}
            onConfirm={() => handlePromote(confirmModal.uid)}
            onClose={() => setConfirmModal(null)}
          />
        )}
        {confirmModal?.action === 'leave' && (
          <ConfirmModal
            title="Leave Family"
            body="Are you sure you want to leave this family? Your access to all family entitlements will be revoked."
            actionLabel="Leave"
            danger
            onConfirm={handleLeave}
            onClose={() => setConfirmModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
